import { createAuth0 } from '@auth0/auth0-vue';
import { SessionStorage } from 'quasar';
import { boot } from 'quasar/wrappers';
import { DlaClient } from 'src/services/dla-api';
import { watch } from 'vue';
import { useBannerFlag } from 'src/composables/useBannerFlag';
import { useUser } from 'src/composables/useUser';
import type { RouteLocationNormalized, Router } from 'vue-router';
import Hotjar from '@hotjar/browser';
import { useTelemetry } from '@data-literacy-academy/use-telemetry';
import { user_logged_in } from 'src/utils/telemetryEvents';

const { set: setMyCertificationsBanner } = useBannerFlag(
  'showMyCertificationsBanner',
);

let loginTracked = false;

const LOGIN_PATHS = ['/', '/login'];
const SSO_PATHS = ['/auto-login', '/auto-login/', '/sso'];
const ALL_LOGIN_PATHS = LOGIN_PATHS.concat(SSO_PATHS);
const NO_REDIRECT_PATHS = new Set(['/login', ...SSO_PATHS]);

export const auth0 = createAuth0({
  domain: 'auth.dl-academy.tech',
  clientId: process.env.DEBUGGING
    ? '8PaToKktIO8DZV7pjWOfzmiVEaU7mBnT'
    : 'NdGwMlGkC7EjpMx1E7Gvk33foNHQhpn8',
  cacheLocation: 'localstorage',
  useRefreshTokens: true,
  authorizationParams: {
    redirect_uri: window.location.origin,
    callback_url: window.location.origin,
    audience: 'dla',
  },
});

const isAuthenticated = (): boolean => {
  return !!(auth0.isAuthenticated.value || SessionStorage.getItem('user'));
};

export async function handleAuthCheck(
  to: RouteLocationNormalized,
  router: Router,
) {
  if (isAuthenticated()) {
    const user = await new DlaClient().get('/users/identify');
    const { setUser } = useUser();
    setUser(user);
    Hotjar.identify(user.id, {});

    const deepLinkUrl = SessionStorage.getItem<string>('deepLinkUrl');
    if (deepLinkUrl) {
      SessionStorage.remove('deepLinkUrl');
      router.push(deepLinkUrl);
      return;
    }

    if (!loginTracked) {
      loginTracked = true;
      useTelemetry().trackEvent({
        type: user_logged_in,
        page_id: to.path,
        timestamp: Date.now(),
        payload: { user_id: user.id, email: user.email },
      });
    }

    if (LOGIN_PATHS.includes(to.path)) {
      router.push('/learning/dashboard');
    }
    return;
  }

  if (to.path && !ALL_LOGIN_PATHS.includes(to.path)) {
    SessionStorage.set('deepLinkUrl', to.path);
    if (to.path === '/courses') {
      setMyCertificationsBanner();
    }
  }

  if (!NO_REDIRECT_PATHS.has(to.path)) {
    router.push({
      path: '/login',
      query: to.query,
    });
  }
}

export function checkAuth(to: RouteLocationNormalized, router: Router) {
  if (to.meta.open) {
    return;
  }

  if (!auth0.isLoading.value) {
    handleAuthCheck(to, router);
    return;
  }

  const unwatch = watch(auth0.isLoading, (loading) => {
    if (loading) {
      return;
    }
    unwatch();
    handleAuthCheck(to, router);
  });
}

export default boot(async ({ app, router }) => {
  app.use(auth0);
  // Reset the flag when the user logs out so a subsequent login is tracked
  watch(auth0.isAuthenticated, (authenticated) => {
    if (!authenticated) {
      loginTracked = false;
    }
  });
  router.beforeEach((to, from) => {
    if (
      from.matched.length > 0 &&
      to.name === from.name &&
      to.path === from.path
    )
      return;
    checkAuth(to, router);
  });
});
